import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../../i18n';

const initialState = {
  roles: [],
  total: 0,
  page: 1,
  pageSize: 10,
  sortColumn: 'name',
  sortDirection: 'asc',
  filterName: '',
  loading: false,
  error: null,
  successMessage: null,
  selectedRole: null,
  defaultPermissions: null, // Default permissions from /roles/init
  modules: null, // Module metadata (title, description) from /roles/init
};

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    // Action to trigger fetch init permissions
    fetchInitPermissions: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitPermissionsSuccess: (state, action) => {
      state.loading = false;
      state.defaultPermissions = action.payload.permissions;
      state.modules = action.payload.modules || null;
      state.error = null;
    },
    fetchInitPermissionsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch roles (saga will handle this)
    fetchRoles: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchRolesSuccess: (state, action) => {
      state.loading = false;
      state.roles = action.payload.data || [];
      // Extract pagination info from meta object
      state.total = action.payload.meta?.total || 0;
      state.page = action.payload.meta?.page || 1;
      state.pageSize = action.payload.meta?.size || 10;
      state.error = null;
    },
    fetchRolesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger add role
    addRole: (state) => {
      state.loading = true;
      state.error = null;
    },
    addRoleSuccess: (state, action) => {
      state.loading = false;
      state.roles.push(action.payload);
      state.error = null;
      state.successMessage = i18n.t('roles.success.created');
    },
    addRoleFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch single role
    fetchRoleById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchRoleByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedRole = action.payload;
      state.error = null;
    },
    fetchRoleByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger update role
    updateRole: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateRoleSuccess: (state, action) => {
      state.loading = false;
      const index = state.roles.findIndex((role) => role.id === action.payload.id);
      if (index !== -1) {
        state.roles[index] = action.payload;
      }
      state.error = null;
      state.successMessage = i18n.t('roles.success.updated');
    },
    updateRoleFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger delete role
    deleteRole: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteRoleSuccess: (state, action) => {
      state.loading = false;
      state.roles = state.roles.filter((role) => role.id !== action.payload);
      state.error = null;
      state.successMessage = i18n.t('roles.success.deleted');
    },
    deleteRoleFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger bulk delete roles
    bulkDeleteRoles: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteRolesSuccess: (state, action) => {
      state.loading = false;
      state.roles = state.roles.filter((role) => !action.payload.includes(role.id));
      state.error = null;
      state.successMessage = i18n.t('roles.success.bulkDeleted', { count: action.payload.length });
    },
    bulkDeleteRolesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Set selected role
    setSelectedRole: (state, action) => {
      state.selectedRole = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    // Clear selected role
    clearSelectedRole: (state) => {
      state.selectedRole = null;
    },
    // Set pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page when page size changes
    },
    // Set sort
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    // Set filter
    setFilterName: (state, action) => {
      state.filterName = action.payload;
      state.page = 1; // Reset to first page when filter changes
    },
  },
});

// Export actions
export const {
  fetchInitPermissions,
  fetchInitPermissionsSuccess,
  fetchInitPermissionsFailure,
  fetchRoles,
  fetchRolesSuccess,
  fetchRolesFailure,
  fetchRoleById,
  fetchRoleByIdSuccess,
  fetchRoleByIdFailure,
  addRole,
  addRoleSuccess,
  addRoleFailure,
  updateRole,
  updateRoleSuccess,
  updateRoleFailure,
  deleteRole,
  deleteRoleSuccess,
  deleteRoleFailure,
  bulkDeleteRoles,
  bulkDeleteRolesSuccess,
  bulkDeleteRolesFailure,
  setSelectedRole,
  clearError,
  clearSuccessMessage,
  clearSelectedRole,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
} = rolesSlice.actions;

// Selectors
export const selectRoles = (state) => state.roles.roles;
export const selectRolesLoading = (state) => state.roles.loading;
export const selectRolesError = (state) => state.roles.error;
export const selectRolesSuccessMessage = (state) => state.roles.successMessage;
export const selectSelectedRole = (state) => state.roles.selectedRole;
export const selectRolesTotal = (state) => state.roles.total;
export const selectRolesPage = (state) => state.roles.page;
export const selectRolesPageSize = (state) => state.roles.pageSize;
export const selectRolesSortColumn = (state) => state.roles.sortColumn;
export const selectRolesSortDirection = (state) => state.roles.sortDirection;
export const selectRolesFilterName = (state) => state.roles.filterName;
export const selectDefaultPermissions = (state) => state.roles.defaultPermissions;
export const selectModules = (state) => state.roles.modules;

// Export reducer
export default rolesSlice.reducer;
